/**
 * Park a horizontal listing row while it is far off screen.
 *
 * For feeds that append rows without end. Home on an iPhone went white after
 * about a dozen load-mores: every row ever appended stayed mounted with all its
 * cards and decoded photos, the tab ran out of memory, and iOS killed it. That
 * leaves no JS error to report; prod showed the deep sessions ending in a fresh
 * page view of "/" seconds after the last load-more.
 *
 * Parked means the caller unmounts the slider and the box holds the height it
 * had. Whatever the row has loaded stays in the caller's state, so a row that
 * comes back keeps every page it fetched and opens on the slide it was left on.
 * Ads inside the slider unmount with it, which is what lets their GAM slots be
 * destroyed (see components/ad-slot-reaper.ts) and their budget reused.
 */

import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import { currentPageKey } from '../utils/browse-list-cache';
import { isPageEnteredByBack } from '../utils/navigation-direction';

/**
 * How far past the screen, above and below, a row keeps its cards. Wide
 * enough that a fast fling lands on rows already painted.
 */
export const WINDOW_MARGIN_PX = 1500;

/**
 * Rows and grid cells kept for Back, across pages, past which the oldest go.
 * A grid keeps one per tile (components/legacy-listings.tsx), and a deep
 * search reaches 200, so a few pages of them must fit.
 */
const MAX_KEPT_ROWS = 3000;

/** Each row's height and slide when its page was left, by page and row. */
const keptRows = new Map<string, { height: number; slide: number }>();

/** Test seam. */
export function clearKeptRows(): void {
  keptRows.clear();
}

export interface UseWindowedRowOptions {
  /** Off: the row is never parked and no observer is created. */
  enabled: boolean;
  /** Nothing to hold until the row has cards; the observer waits for them. */
  hasCards: boolean;
  /** What is watched: the whole row, heading included. Defaults to the box. */
  rowRef?: RefObject<HTMLElement | null>;
  /** The slider's ref, read for the slide to reopen on. */
  swiperRef?: RefObject<{ swiper: SwiperType } | null>;
  /**
   * The row's name on its page. With one, Back opens the row parked at the
   * height it had, and only the rows near the screen mount their slider.
   *
   * Back to home 12 screens down mounted 41 sliders at once: 9 s of main
   * thread, 2,000 forced style recalcs, the product page on screen the whole
   * time (prod, 2026-10-26). A visit mounts them a page at a time; Back paid
   * for all of them in one go.
   */
  keepKey?: string;
}

export interface UseWindowedRowResult {
  /** Put on the element that wraps the slider; it holds the height while parked. */
  boxRef: RefObject<HTMLDivElement | null>;
  /** True while the slider should not be rendered. */
  parked: boolean;
  /** Pass to the slider as `initialSlide`. */
  initialSlide: number;
}

export function useWindowedRow({ enabled, hasCards, rowRef, swiperRef, keepKey }: UseWindowedRowOptions): UseWindowedRowResult {
  const boxRef = useRef<HTMLDivElement>(null);
  const [rowKey] = useState(() => (keepKey ? `${currentPageKey()}|${keepKey}` : ''));
  const [kept] = useState(() => (enabled && rowKey && isPageEnteredByBack() ? keptRows.get(rowKey) ?? null : null));
  const [parked, setParked] = useState(!!kept);
  const [initialSlide, setInitialSlide] = useState(kept?.slide ?? 0);

  // Back: hold the height before the first paint, so the page is as tall as it was.
  useLayoutEffect(() => {
    if (kept && boxRef.current) boxRef.current.style.height = `${kept.height}px`;
  }, [kept]);

  // Leaving: note the height and slide for Back. The box holds its height
  // while parked, so either way this reads the row's real size.
  const slideRef = useRef(initialSlide);
  useEffect(() => {
    slideRef.current = initialSlide;
  }, [initialSlide]);
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!rowKey || !box) return undefined;
    return () => {
      const height = box.offsetHeight;
      if (height === 0) return;
      keptRows.delete(rowKey);
      keptRows.set(rowKey, { height, slide: swiperRef?.current?.swiper?.activeIndex ?? slideRef.current });
      while (keptRows.size > MAX_KEPT_ROWS) {
        // A Map iterates in insertion order, so the first key is the oldest.
        const [oldest] = keptRows.keys();
        keptRows.delete(oldest);
      }
    };
  }, [rowKey, swiperRef]);

  useEffect(() => {
    if (!enabled || !hasCards || typeof IntersectionObserver === 'undefined') return;
    // Every caller renders the watched element whenever the row has cards.
    const row = (rowRef ?? boxRef).current!;

    const observer = new IntersectionObserver(
      (entries) => {
        // The LAST entry is the row's current state. When the main thread is
        // busy (a fast fling while rows are rendering) one callback can carry
        // several, oldest first; reading the first parked a row that had
        // already come back on screen, and nothing ever unparked it.
        const entry = entries[entries.length - 1];
        // A row with no slider box yet (showcase.tsx draws its heading first).
        const box = boxRef.current;
        if (!box) return;
        if (entry.isIntersecting) {
          setParked(false);
          return;
        }
        // Not laid out yet (or dismissed): nothing to hold, so do not park.
        const height = box.offsetHeight;
        if (height === 0) return;
        // Measured while the cards are still mounted, so the placeholder is the
        // row's real height. Imperative because it is a measurement, not a
        // style: the rows above the screen must not change height, or the page
        // jumps under the reader (iOS Safari has no scroll anchoring).
        box.style.height = `${height}px`;
        setInitialSlide(swiperRef?.current?.swiper?.activeIndex ?? 0);
        setParked(true);
      },
      { rootMargin: `${WINDOW_MARGIN_PX}px 0px` }
    );

    observer.observe(row);
    return () => observer.disconnect();
  }, [enabled, hasCards, rowRef, swiperRef]);

  // Back on screen: the cards are in the DOM again, so let the box size to
  // them. A layout effect, so the held height is never released for a frame
  // with nothing inside it.
  useLayoutEffect(() => {
    if (!parked && boxRef.current) boxRef.current.style.height = '';
  }, [parked]);

  return { boxRef, parked, initialSlide };
}
